import { useContext, useState, useEffect, useMemo, useRef } from 'react';
import { Dropdown, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { usePagination } from '@ajna/pagination';
import WorkspaceContext from '../context/WorkspaceContext';
import SearchBarHeader from '../components/searchBar/SearchBarHeader';
import ListCard from '../components/ListCard/ListCard';
import { Button, Pagination } from 'antd';
import CKAN from '../lib/CKAN';
import DataModal from '../components/modal/DataModal';
import APIsModal from '../components/modal/APIsModal';
import PermissionsHelper from '../util/PermissionsHelper';
import Sparcal from '../lib/Sparcal';
import DeleteModal from '../components/modal/DeleteModal';
import './DataCatalogNew.css';
import { ITS_DATA_VALID_YEARS } from '../util/ITS';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import CatalogContext from '../context/CatalogContext';
import { expandDatasetForMap } from '../map/utils';
import FilterCKAN from '../lib/FilterCKAN';
import { FilterData } from '../components/searchBar/FilterCollection';

// URL search params <-> the catalog's search inputs and paging, e.g.
// /catalog?q=carbon&collection=California+Landscape+Metrics&page=2&size=50
// page/size are left out at their defaults (1 and DEFAULT_PAGE_SIZE).
const URL_PARAMS = ['q', 'collection', 'pillar', 'element'];
const searchToParams = (query, collection, pillar, element) => {
  const params = new URLSearchParams();
  [query, collection, pillar, element].forEach((value, i) => {
    if (value?.trim()) params.set(URL_PARAMS[i], value.trim());
  });
  return params;
};

// Smallest option first - below it there's only one page.
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

// Just the search part of the URL, so paging alone doesn't re-run a search.
const searchKeyOf = (params) =>
  searchToParams(...URL_PARAMS.map((key) => params.get(key))).toString();
const pageOf = (params) => {
  const page = parseInt(params.get('page'), 10);
  return page > 0 ? page : 1;
};
const pageSizeOf = (params) => {
  const size = parseInt(params.get('size'), 10);
  return PAGE_SIZE_OPTIONS.includes(size) ? size : DEFAULT_PAGE_SIZE;
};

const DataCatalogNew = (props) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // The params of the search on screen, so URL changes we made ourselves
  // aren't re-run as if someone had navigated to them.
  const appliedParams = useRef(null);
  const {
    ckanData,
    setCkanData,
    collectionNames,
    setCollectionNames,
    pillar,
    setPillar,
    element,
    setElement,
    collectionsList,
    setCollectionList,
    pillarList,
    setPillarList,
    elementList,
    setElementList,
    currentDatasets,
    setCurrentDatasets,
    numDatasets,
    setNumDatasets,
    searchResults,
    setSearchResults,
    searchQuery,
    setSearchQuery,
    searchFlag,
    setSearchFlag,
    searchType,
    spatialQuery,
    setSpatialQuery,
  } = useContext(CatalogContext);

  const [isLoading, setIsLoading] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalImage, setModalImage] = useState(null);
  const [apisModalShow, setAPIsModalShow] = useState(false);
  const [apisModalData, setAPIsModalData] = useState(null);
  const [deleteData, setDeleteData] = useState(null);
  const [toastModalTitle, setToastModalTitle] = useState(null);
  const [toastModalBody, setToastModalBody] = useState(null);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [errorMessage, setErrorMessage] = useState(null);

  const { selectedDatasets, setSelectedDatasets } =
    useContext(WorkspaceContext);

  // const [bbox, setBbox] = useState(false);
  const viewCreateButton = PermissionsHelper(props.user.roles, 'publisher');

  const { currentPage, setCurrentPage, pageSize, setPageSize, offset } =
    usePagination({
      total: numDatasets,
      initialState: {
        pageSize: pageSizeOf(searchParams),
        currentPage: pageOf(searchParams),
        isDisabled: false,
      },
    });

  // Header count
  useMemo(() => {
    const fetchData = async () => {
      let errorMessage = null;
      setIsLoading(true);

      const ckanDatasets = await CKAN.getDatasets();
      if (ckanDatasets?.length) {
        setCkanData(ckanDatasets);
      } else {
        errorMessage = ckanDatasets?.message;
      }
      setErrorMessage(errorMessage);
      setIsLoading(false);
    };
    if (!ckanData) {
      fetchData();
    }
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (ckanData) {
      if (!searchFlag) {
        // without search
        const data = ckanData.slice(offset, offset + pageSize);
        setCurrentDatasets(data);
        setNumDatasets(ckanData.length);
        setIsLoading(false);
      } else {
        // on search
        // for substring_search
        const dataToDisplay = searchResults.slice(offset, offset + pageSize); // offset is the start index based on current page number
        setCurrentDatasets(dataToDisplay);
        setNumDatasets(searchResults.length);
        setIsLoading(false);
      }
    }
  }, [currentPage, pageSize, offset, searchResults, ckanData]);

  useEffect(() => {
    if (state?.selectedBoundary) {
      setPillarList(null);
      setElementList(null);
      setPillar('');
      setElement('');
      setCurrentPage(1);
      setSpatialQuery('');
      setSearchQuery('');
      handleSearchClick('', 'Boundaries', '', '', { replaceUrl: true });
      setCollectionNames('Boundaries');
    } else if (state?.selectedIts) {
      setPillarList(null);
      setElementList(null);
      setPillar('');
      setElement('');
      setCurrentPage(1);
      setSpatialQuery('');
      setSearchQuery('');
      handleSearchClick(
        '',
        'California Interagency Treatment Tracking System',
        '',
        '',
        { replaceUrl: true }
      );
      setCollectionNames('California Interagency Treatment Tracking System');
    } else if (state?.selectedClm) {
      setPillarList(null);
      setElementList(null);
      setPillar('');
      setElement('');
      setCurrentPage(1);
      setSpatialQuery('');
      setSearchQuery('');
      handleSearchClick('', 'California Landscape Metrics', '', '', {
        replaceUrl: true,
      });
      setCollectionNames('California Landscape Metrics');
    }
  }, [state]);

  const handleFilterChange = (collection, pillar, element) => {
    handleSearchClick(searchQuery, collection, pillar, element);
  };
  // A page past the end (an old link, or fewer results now) shows the last
  // page instead of an empty list.
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(numDatasets / pageSize));
    if (numDatasets > 0 && currentPage > lastPage) {
      setCurrentPage(lastPage);
      const params = new URLSearchParams(searchParams);
      if (lastPage > 1) params.set('page', lastPage);
      else params.delete('page');
      setSearchParams(params, { replace: true });
    }
  }, [numDatasets, pageSize, currentPage]);

  const handlePageChange = (nextPage, nextPageSize) => {
    let page = nextPage;
    if (nextPageSize !== pageSize) {
      // Stay on the page that holds the first dataset currently shown.
      page = Math.floor(offset / nextPageSize) + 1;
      setPageSize(nextPageSize);
    }
    setCurrentPage(page);
    // Each page gets a history entry, so Back goes to the previous page.
    const params = new URLSearchParams(searchParams);
    if (page > 1) params.set('page', page);
    else params.delete('page');
    if (nextPageSize !== DEFAULT_PAGE_SIZE) params.set('size', nextPageSize);
    else params.delete('size');
    setSearchParams(params);
  };

  // The top and bottom paginators share this, so they always show the same
  // page and page size.
  const renderPagination = () =>
    numDatasets > PAGE_SIZE_OPTIONS[0] ? (
      <Pagination
        current={currentPage}
        pageSize={pageSize}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        showSizeChanger
        onChange={handlePageChange}
        total={numDatasets}
      />
    ) : null;
  const handleChange = (event) => {
    setSearchQuery(event.target.value);
    // setSearchFlag(false)
  };

  // Records a search in the URL. Each new search gets a history entry so Back
  // returns to the previous one; `replace` is for searches the user didn't
  // start themselves (a collection link, restoring a kept search). A new
  // search starts on page 1, but keeps the chosen page size.
  const syncUrl = (params, replace = false) => {
    appliedParams.current = params.toString();
    if (pageSize !== DEFAULT_PAGE_SIZE) params.set('size', pageSize);
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace });
    }
  };

  // Load the filter dropdowns a URL's collection/pillar needs (normally
  // they're filled in as the user picks each level).
  const restoreFilterLists = async (collection, pillar) => {
    if (!collection) {
      setPillarList(null);
      setElementList(null);
      return;
    }
    const pillars = FilterData(
      (await FilterCKAN.getPillarData(collection)) || []
    );
    setPillarList(pillars.length ? pillars : null);
    if (!pillar) {
      setElementList(null);
      return;
    }
    const elements = FilterData(
      (await FilterCKAN.getElementData(collection, pillar)) || []
    );
    setElementList(elements.length ? elements : null);
  };

  // Follow the URL on first load, a shared link, or Back/Forward: run its
  // search if that changed, otherwise just move to its page.
  useEffect(() => {
    const current = searchKeyOf(searchParams);
    const urlPage = pageOf(searchParams);
    const urlPageSize = pageSizeOf(searchParams);
    if (current === appliedParams.current) {
      if (urlPageSize !== pageSize) setPageSize(urlPageSize);
      if (urlPage !== currentPage) setCurrentPage(urlPage);
      return;
    }
    // Collection links from other pages pass their filter in router state;
    // the effect above runs that search and writes the URL.
    if (
      !current &&
      (state?.selectedBoundary || state?.selectedIts || state?.selectedClm)
    ) {
      return;
    }
    if (appliedParams.current === null && !current) {
      // Back on plain /catalog: keep the search still held in context and
      // show it in the URL.
      const kept = searchFlag
        ? searchToParams(searchQuery, collectionNames, pillar, element)
        : new URLSearchParams();
      if (kept.toString()) syncUrl(kept, true);
      else appliedParams.current = '';
      return;
    }
    appliedParams.current = current;
    const [query, collection, urlPillar, urlElement] = URL_PARAMS.map(
      (key) => searchParams.get(key) || ''
    );
    setSearchQuery(query);
    setCollectionNames(collection);
    setPillar(urlPillar);
    setElement(urlElement);
    restoreFilterLists(collection, urlPillar);
    setPageSize(urlPageSize);
    handleSearchClick(query, collection, urlPillar, urlElement, {
      fromUrl: true,
      page: urlPage,
    });
  }, [searchParams]);

  const handleSearchClick = async (
    newSearchQuery,
    newCollection,
    newPillar,
    newElement,
    { replaceUrl = false, fromUrl = false, page = 1 } = {}
  ) => {
    setSearchedQuery(newSearchQuery);
    // Searches read from the URL are already there (with their page).
    if (!fromUrl) {
      syncUrl(
        searchToParams(newSearchQuery, newCollection, newPillar, newElement),
        replaceUrl
      );
    }
    let errorMessage = null;
    if (
      (collectionNames && newSearchQuery) ||
      searchType === 'substring_search' ||
      spatialQuery
    ) {
      setIsLoading(true);
      const results = await CKAN.searchDatasets({
        searchString: newSearchQuery,
        collectionNames: newCollection,
        pillar: newPillar,
        element: newElement,
      });
      if (results?.status) {
        errorMessage = results?.message;
      } else {
        setSearchFlag(true);
        setSearchResults(results);
        setCurrentPage(page);
      }
      setIsLoading(false);
    } else {
      setSearchedQuery('');
      setSearchFlag(false);
      setSearchQuery('');
      setSearchResults([]);
      // setSelectedOrg('');
      // setSelectedTag('');
      setCollectionNames('');
      setPillar('');
      setElement('');
      setCurrentPage(1);
      setSpatialQuery('');
    }
    setErrorMessage(errorMessage);
  };

  const handleClearClick = async () => {
    syncUrl(new URLSearchParams());
    let errorMessage = null;
    setIsLoading(true);
    setSearchQuery('');
    setPillarList(null);
    setElementList(null);
    setCollectionNames('');
    setPillar('');
    setElement('');
    const results = await CKAN.searchDatasets({
      searchString: '',
      collectionNames: '',
      pillar: '',
      element: '',
    });
    if (results?.status) {
      errorMessage = results?.message;
    } else {
      setSearchFlag(true);
      setSearchResults(results);
    }
    setIsLoading(false);
    setErrorMessage(errorMessage);
  };

  const inWorkspace = (dataset) => {
    if (!dataset) return false;
    for (let i = 0; i < selectedDatasets.length; i++) {
      if (selectedDatasets[i].id === dataset.id) {
        return true;
      }
    }

    return false;
  };
  const addToWorkspace = (dataset) => {
    if (!inWorkspace(dataset)) {
      let newSelectedDatasets = [...selectedDatasets];
      newSelectedDatasets.push(...expandDatasetForMap(dataset));
      setSelectedDatasets(newSelectedDatasets);
      setToastModalTitle('Added to  Map Viewer');
      setToastModalBody(
        'Dataset was added to your Map Viewer. You can add more datasets from the Catalog or go to the Map Viewer to view and analyze them.'
      );
      if (dataset.name.search('its-interagency') !== -1) {
        const itemKey = props.getItemKey(dataset);
        let filterItems = { ...props.filterItems };
        filterItems[itemKey] = { ...ITS_DATA_VALID_YEARS };
        props.setFilterItems(filterItems);
      }
    }
  };

  const removeFromWorkspace = (dataset) => {
    // A multi-layer dataset occupies one workspace entry per layer, all
    // sharing its id (see expandDatasetForMap), so remove every one.
    setSelectedDatasets(selectedDatasets.filter((e) => e.id !== dataset.id));
  };
  // Map
  // let mapRef = useRef(null); // Ref to keep track of map instance
  // let rectangleRef = useRef(null);
  useEffect(() => {
    // setMap();
  }, []);

  // TODO support map search
  //   const setMap = () => {
  //     mapRef.current?.remove();
  //     mapRef.current = null;
  //     if (typeof window !== 'undefined') {
  //       const L = require('leaflet'); // Dynamically require leaflet
  //       require('leaflet-draw'); // Dynamically require leaflet-draw

  //       if (!mapRef.current) {
  //         // Initialize the map centered on Yosemite National Park
  //         mapRef.current = L.map('map').setView([37.0902, -95.7129], 3);

  //         // // Add OpenStreetMap tiles
  //         // L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
  //         //   attribution: '&copy; OpenStreetMap contributors',
  //         // }).addTo(mapRef.current)

  //         L.tileLayer(
  //           'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}',
  //           {
  //             attribution: 'Tiles &copy; Esri',
  //           }
  //         ).addTo(mapRef.current);

  //         // Initialize the drawing tool
  //         const drawControl = new L.Control.Draw({
  //           draw: {
  //             polygon: false,
  //             polyline: false,
  //             circle: false,
  //             marker: false,
  //             circlemarker: false,
  //             rectangle: true, // Enable rectangle drawing (bounding box)
  //           },
  //         });
  //         mapRef.current?.addControl(drawControl);

  //         // Handle the "draw:created" event when a rectangle is drawn
  //         mapRef.current?.on(L.Draw.Event.CREATED, (event) => {
  //           const layer = event.layer;
  //           if (rectangleRef.current && mapRef.current) {
  //             mapRef.current.removeLayer(rectangleRef.current);
  //           }
  //           rectangleRef.current = layer;
  //           layer.addTo(mapRef.current);
  //           setBbox(true);

  //           // Get the bounding box coordinates
  //           const bounds = layer.getBounds();
  //           const southWest = bounds.getSouthWest();
  //           const northEast = bounds.getNorthEast();

  //           const bbox = [
  //             southWest.lng, // min longitude
  //             southWest.lat, // min latitude
  //             northEast.lng, // max longitude
  //             northEast.lat, // max latitude
  //           ];

  //           const spatialQ = `ext_bbox=${bbox.join(',')}`;
  //           // console.log(spatialQ)
  //           setSpatialQuery(spatialQ);
  //           console.log('toast3');
  //           //   toast({
  //           //     title: 'Bounding box is selected.',
  //           //     description: 'Click on search to execute!',
  //           //     status: 'info',
  //           //     variant: 'left-accent',
  //           //     duration: 2000,
  //           //     isClosable: true,
  //           //     position: 'top-right',
  //           //   });
  //         });
  //       }
  //     }
  //   };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        height: '100vh',
        backgroundColor: '#f8faf9',
      }}
    >
      {modalShow && (
        <DataModal
          getGisService={props.getGisService}
          data={modalData}
          image={modalImage}
          onHide={() => {
            setModalShow(false);
            setModalData(null);
            setModalImage(null);
          }}
        />
      )}
      {deleteData && (
        <DeleteModal
          data={deleteData}
          user={props.user}
          // onDelete={async () => {
          //   Sparcal.deleteDataset(props.user, deleteData.id);
          //   setSearchFlag(true);
          //   setCkanData(
          //     ckanData.filter((data) => {
          //       return data.id !== deleteData.id;
          //     })
          //   );
          //   // await handleSearchClick();
          //   setSearchFlag(false);
          // }}
          onHide={() => {
            setDeleteData(null);
          }}
        />
      )}
      {apisModalShow && (
        <APIsModal
          data={apisModalData}
          onHide={() => {
            setAPIsModalShow(false);
            setAPIsModalData(null);
          }}
        />
      )}
      {toastModalTitle && (
        <ToastContainer
          style={{ position: 'fixed', paddingTop: '60px' }}
          position="top-center"
        >
          <Toast
            onClose={() => {
              setToastModalTitle(null);
              setToastModalBody(null);
            }}
            delay={6000}
            autohide
            style={{ width: '500px' }}
          >
            <Toast.Header style={{ backgroundColor: '#A7BFB5' }}>
              <strong className="me-auto">{toastModalTitle}</strong>
            </Toast.Header>
            {toastModalBody && (
              <Toast.Body style={{ backgroundColor: 'white' }}>
                {toastModalBody}
              </Toast.Body>
            )}
          </Toast>
        </ToastContainer>
      )}
      <div
        style={{
          backgroundSize: 'cover',
          // Extra room above the title so it isn't pressed against the top.
          margin: '64px 10% 28px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <h1
            style={{
              color: '#2C3E37',
              fontWeight: 'bolder',
              fontSize: '36px',
              marginBottom: '0px',
            }}
          >
            Catalog
          </h1>
          {/* {viewCreateButton && (
            <Dropdown variant="primary">
              <Dropdown.Toggle
                style={{
                  backgroundColor: '#e3b448',
                  fontWeight: 'bolder',
                  fontSize: '14px',
                  border: 0,
                }}
              >
                Register or Edit (Data Ingestion){' '}
              </Dropdown.Toggle>
              <Dropdown.Menu
                style={{
                  width: '262px',
                  backgroundColor: '#f3c689',
                  inset: '-5px auto auto 0p',
                  fontWeight: 'bolder',
                  fontSize: '14px',
                }}
              >
                <Dropdown.Item
                  onClick={() => {
                    navigate('/catalog/new');
                    props.setAddDataType('CLM');
                  }}
                >
                  Register or Edit CLM dataset
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => {
                    navigate('/catalog/new');
                    props.setAddDataType('Generic');
                  }}
                >
                  Register or Edit Generic dataset
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          )} */}
        </div>
        <SearchBarHeader
          searchQuery={searchQuery}
          handleSearchClick={() => {
            handleSearchClick(searchQuery, collectionNames, pillar, element);
          }}
          handleClearClick={handleClearClick}
          handleChange={handleChange}
          collectionNames={collectionNames}
          setCollectionNames={setCollectionNames}
          collectionsList={collectionsList}
          setCollectionList={setCollectionList}
          pillar={pillar}
          setPillar={setPillar}
          pillarList={pillarList}
          setPillarList={setPillarList}
          element={element}
          elementList={elementList}
          setElementList={setElementList}
          setElement={setElement}
          handleFilterChange={handleFilterChange}
          setErrorMessage={setErrorMessage}
        ></SearchBarHeader>
      </div>
      <div
        style={{
          backgroundColor: '#CEDBD6',
          backgroundSize: 'cover',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '15px 35px 35px 35px',
        }}
      >
        {isLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignContent: 'center',
            }}
          >
            <Spinner animation="border" variant="success" />
          </div>
        ) : (
          <>
            {renderPagination()}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                paddingTop: '8px',
              }}
            >
              {currentDatasets.length > 0 ? (
                currentDatasets.map((dataset, idx) => {
                  return (
                    <ListCard
                      key={dataset.name + idx}
                      dataset={dataset}
                      setModalData={setModalData}
                      setModalShow={setModalShow}
                      setModalImage={setModalImage}
                      setAPIsModalData={setAPIsModalData}
                      setAPIsModalShow={setAPIsModalShow}
                      setDeleteData={() => {
                        setDeleteData(dataset);
                      }}
                      addToWorkspace={(data) => addToWorkspace(data)}
                      removeFromWorkspace={(data) => removeFromWorkspace(data)}
                      inWorkspace={(data) => inWorkspace(data)}
                      user={props.user}
                      getGisService={props.getGisService}
                    />
                  );
                })
              ) : (
                <div>
                  {!isLoading && (
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                      <h2
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        {errorMessage ? (
                          <div>
                            Datasets are currently unavailable <hr />
                            <h5 style={{ justifySelf: 'center' }}>
                              If issue persists please submit a feedback ticket
                            </h5>
                          </div>
                        ) : (
                          <>No datasets found</>
                        )}
                      </h2>

                      {searchQuery && (
                        <>
                          Do you wish to clear your search "{searchedQuery}"?
                          <br />
                          <Button
                            type="link"
                            onClick={() => {
                              handleSearchClick(
                                '',
                                collectionNames,
                                pillar,
                                element
                              );
                              setSearchQuery('');
                            }}
                          >
                            Clear
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            {renderPagination()}
          </>
        )}
      </div>
    </div>
  );
};
export default DataCatalogNew;
